import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CContainer, CRow, CCol, CCard, CCardHeader, CCardBody, CButton,
  CSpinner, CFormCheck, CInputGroup, CBadge, CAlert, CFormInput,
  CFormLabel, CButtonGroup, CListGroup, CListGroupItem, CAccordion,
  CAccordionItem, CAccordionHeader, CAccordionBody, CTable, CTableHead,
  CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CTooltip
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft, cilPencil, cilCheck, cilX, cilCamera, cilWarning,
  cilTask, cilCarAlt, cilChart, cilMove, cilCommentBubble, cilSync,
  cilOptions, cilInfo, cilMonitor
} from "@coreui/icons";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import enUS from "date-fns/locale/en-US";
import dayjs from "dayjs";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";

// Компоненты
import Loading from "../../components/Loading/Loading";
import RoiEditor from "../../components/RoiEditor/RoiEditor";
import AnalyticsBlock from "../../components/AnalyticsBlock/AnalyticsBlock";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import CameraSettings from "./components/CameraSettings";
import CameraModals from "./components/CameraModals";
import MultiCameraMonitor from "../../components/MultiCameraMonitor/MultiCameraMonitor";

// Утилиты и Хуки
import { formatToCameraUTC, formatDuration } from "../../utils/cameraUtils";
import { useCameraAnalytics } from "./hooks/useCameraAnalytics";
import "./CameraDetailPage.css";

//Утилита права
import { getUserRoles } from "../../utils/authUtils";

registerLocale("ru", ru);
registerLocale("en", enUS);

const CameraDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { cameraId } = useParams();

  // Получаем права
  const { is_staff, is_superuser } = getUserRoles();
  const hasAdminRights = is_staff || is_superuser;

  const [camera, setCamera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState({});
  const [imageSizes, setImageSizes] = useState({ queue: { naturalWidth: 0, naturalHeight: 0 } });
  const [polygons, setPolygons] = useState({ queue: { initial: [], current: [] } });

  const [sourceCameraNames, setSourceCameraNames] = useState({});
  const [videos, setVideos] = useState([]);

  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [minPercentage, setMinPercentage] = useState(5);
  const [activeView, setActiveView] = useState("general");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [settings, setSettings] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [newOverride, setNewOverride] = useState({ date: new Date(), is_working: false, description: "" });

  // --- НОВЫЕ СТЕЙТЫ ДЛЯ СМЕН И ПЕРЕРЫВОВ ---
  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({ name: "Дневная", start_time: "08:00", end_time: "20:00" });
  const [breaks, setBreaks] = useState([]);
  const [newBreak, setNewBreak] = useState({ start_time: "12:00", end_time: "13:00", description: "Обед" });
  // ----------------------------------------

  const [startDate, setStartDate] = useState(dayjs().startOf("day").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("day").toDate());
  const [isLive, setIsLive] = useState(false);
  const [showRoi, setShowRoi] = useState(true);
  const [roiDebugMode, setRoiDebugMode] = useState(false);
  const [roiDebugInfo, setRoiDebugInfo] = useState(null);

  const [monitorConfig, setMonitorConfig] = useState({
    visible: false,
    mode: 'live',
    initialIsoTime: null
  });

  const [recalculateModalVisible, setRecalculateModalVisible] = useState(false);
  const [recalculationDate, setRecalculationDate] = useState(dayjs().startOf("day").toDate());
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [llmModalVisible, setLlmModalVisible] = useState(false);
  const [llmSyncDate, setLlmSyncDate] = useState(dayjs().startOf("day").toDate());
  const [isLlmSyncing, setIsLlmSyncing] = useState(false);
  const [moveModal, setMoveModal] = useState({ visible: false, log: null, targetGroupId: "" });
  const [blurWarningModalVisible, setBlurWarningModalVisible] = useState(false);

  const abortControllersRef = useRef({});
  const failedCamerasRef = useRef({});

  const {
    autoServiceClassIdToMeta, personnelGeneralMeta, chairMeta, phoneMeta, queueMeta, machineMeta, managerMeta,
    dailySummary, groupedVehicleLogs, dynamicSummary, chairTimelineToShow, phoneTimelineToShow, machineTimelineToShow, managerTimelineToShow
  } = useCameraAnalytics(analytics, camera, t, minPercentage);

  const getSourceIds = useCallback((cam) => {
    if (!cam) return [];
    let ids = [];
    const isCombined = cam.is_combined || cam.is_combined_camera || (cam.additional_cameras && cam.additional_cameras.length > 0);
    if (isCombined) {
      if (cam.parent) ids.push(cam.parent);
      if (cam.additional_cameras && Array.isArray(cam.additional_cameras)) ids.push(...cam.additional_cameras);
      if (cam.secondary_camera && !ids.includes(cam.secondary_camera)) ids.push(cam.secondary_camera);
    } else if (cam.is_duplicate && cam.parent) {
      ids.push(cam.parent);
    } else {
      ids.push(cam.id);
    }
    return [...new Set(ids.filter((id) => id != null))];
  }, []);

  const generalData = useMemo(() => {
    if (!analytics) return null;
    let stats = analytics.mode === "auto_service" ? analytics.general_stats : analytics.general;
    if (!stats) return null;
    return { ...stats, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const chairData = useMemo(() => {
    if (!analytics) return null;
    let stats = analytics.mode === "auto_service" ? analytics.chair_stats : analytics.chair;
    if (!stats) return null;
    return { ...stats, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const phoneData = useMemo(() => {
    if (!analytics) return null;
    let stats = analytics.mode === "auto_service" ? analytics.phone_stats : analytics.phone;
    if (!stats) return null;
    return { ...stats, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const queueData = useMemo(() => {
    if (!analytics) return null;
    let stats = analytics.queue;
    if (!stats) return null;
    return { ...stats, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const machineData = useMemo(() => {
    if (!analytics) return null;
    let stats = analytics.machine;
    if (!stats) return null;
    return { ...stats, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const managerData = useMemo(() => {
    if (!analytics) return null;
    let stats = analytics.manager;
    if (!stats) return null;
    return { ...stats, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const activeTimelineData = useMemo(() => {
    if (!analytics) return [];
    if (analytics.mode === "auto_service") {
      if (activeView === "chair") return chairTimelineToShow;
      if (activeView === "phone") return phoneTimelineToShow;
      return analytics.general_stats?.timeline || [];
    } else {
      if (activeView === "general") return analytics.general?.timeline || [];
      if (activeView === "chair") return analytics.chair?.timeline || [];
      if (activeView === "phone") return analytics.phone?.timeline || [];
      if (activeView === "queue") return analytics.queue?.timeline || [];
      if (activeView === "machine") return analytics.machine?.timeline || [];
      if (activeView === "manager") return analytics.manager?.timeline || [];
    }
    return [];
  }, [analytics, activeView, chairTimelineToShow, phoneTimelineToShow]);

  const activeMeta = useMemo(() => {
    if (activeView === "chair") return chairMeta;
    if (activeView === "phone") return phoneMeta;
    if (activeView === "queue") return queueMeta;
    if (activeView === "machine") return machineMeta;
    if (activeView === "manager") return managerMeta;
    return (analytics?.mode === "auto_service") ? autoServiceClassIdToMeta : personnelGeneralMeta;
  }, [activeView, analytics, chairMeta, phoneMeta, queueMeta, machineMeta, managerMeta, autoServiceClassIdToMeta, personnelGeneralMeta]);

  const fetchNamesForSourceCameras = useCallback((cam) => {
    const ids = getSourceIds(cam);
    const token = localStorage.getItem("token");
    ids.forEach(async (id) => {
      try {
        const res = await axios.get(`/api/cameras/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
        setSourceCameraNames(prev => ({ ...prev, [id]: res.data.name }));
      } catch (e) {
        setSourceCameraNames(prev => ({ ...prev, [id]: `Камера ${id} (Ошибка)` }));
      }
    });
  }, [getSourceIds]);

  const fetchSnapshots = useCallback((cam) => {
    const ids = getSourceIds(cam);
    const token = localStorage.getItem("token");
    const now = Date.now();

    ids.forEach(async (id) => {
      if (failedCamerasRef.current[id] && now - failedCamerasRef.current[id] < 10000) return;

      if (abortControllersRef.current[id]) abortControllersRef.current[id].abort();

      const controller = new AbortController();
      abortControllersRef.current[id] = controller;

      try {
        const isCombined = cam.is_combined || cam.is_combined_camera || (cam.additional_cameras && cam.additional_cameras.length > 0);
        const param = isCombined ? `?source_id=${id}` : "";

        const res = await axios.get(`/api/cameras/${cam.id}/snapshot/${param}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 6000,
          signal: controller.signal
        });

        delete failedCamerasRef.current[id];
        const newUrl = URL.createObjectURL(res.data);

        setSnapshots((prev) => {
          if (prev[id]) URL.revokeObjectURL(prev[id]);
          return { ...prev, [id]: newUrl };
        });
      } catch (e) {
        if (!axios.isCancel(e)) failedCamerasRef.current[id] = Date.now();
      }
    });
  }, [getSourceIds]);

  const fetchOverrides = async () => {
    setLoadingOverrides(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/date-overrides/?camera_id=${cameraId}`, { headers: { Authorization: `Bearer ${token}` } });
      setOverrides(res.data.results);
    } catch (err) { } finally { setLoadingOverrides(false); }
  };

  // --- ФУНКЦИИ ЗАГРУЗКИ СМЕН И ПЕРЕРЫВОВ ---
  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/cameras/${cameraId}/shifts/`, { headers: { Authorization: `Bearer ${token}` } });
      setShifts(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (err) { }
  };

  const fetchBreaks = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/cameras/${cameraId}/breaks/`, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (err) { }
  };

  useEffect(() => {
    const fetchCameraData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const camRes = await axios.get(`/api/cameras/${cameraId}/`, { headers: { Authorization: `Bearer ${token}` } });
        const cameraData = camRes.data;
        setCamera(cameraData);
        fetchNamesForSourceCameras(cameraData);
        setSettings({
          work_start_time: cameraData.work_start_time?.substring(0, 5) || "08:00",
          work_end_time: cameraData.work_end_time?.substring(0, 5) || "18:00",
          timezone_offset: cameraData.timezone_offset || 0,
          vehicle_roi_threshold: Math.round((cameraData.vehicle_roi_threshold || 0) * 100),
          person_roi_threshold: Math.round((cameraData.person_roi_threshold || 0) * 100),
          vehicle_log_merge_seconds: cameraData.vehicle_log_merge_seconds || 120,
          vehicle_roi_occupancy_threshold: ((cameraData.vehicle_roi_occupancy_threshold || 0) * 100).toFixed(2),
          person_roi_occupancy_threshold: ((cameraData.person_roi_occupancy_threshold || 0) * 100).toFixed(3),
          orb_similarity_threshold: cameraData.orb_similarity_threshold || 500,
          vehicle_confidence_threshold: ((cameraData.vehicle_confidence_threshold || 0) * 100).toFixed(1),
          person_confidence_threshold: ((cameraData.person_confidence_threshold || 0) * 100).toFixed(1),
          workday_monday: cameraData.workday_monday,
          workday_tuesday: cameraData.workday_tuesday,
          workday_wednesday: cameraData.workday_wednesday,
          workday_thursday: cameraData.workday_thursday,
          workday_friday: cameraData.workday_friday,
          workday_saturday: cameraData.workday_saturday,
          workday_sunday: cameraData.workday_sunday,
          operating_mode: cameraData.operating_mode || "auto_service",
          count_squatting_as_sitting: cameraData.count_squatting_as_sitting,
          enable_blur: cameraData.enable_blur ?? true,
          has_multiple_shifts: cameraData.has_multiple_shifts || false,
        });

        const ids = getSourceIds(cameraData);
        const roi = cameraData.roi_polygon || {};
        const newPolygons = { queue: { initial: cameraData.queue_roi_polygon || [], current: [] } };
        const newImageSizes = { queue: { naturalWidth: 0, naturalHeight: 0 } };

        if (cameraData.is_combined || cameraData.is_combined_camera || (cameraData.additional_cameras && cameraData.additional_cameras.length > 0)) {
          ids.forEach((id) => {
            newPolygons[id] = { initial: roi[id] || [], current: [] };
            newImageSizes[id] = { naturalWidth: 0, naturalHeight: 0 };
          });
        } else {
          const mainId = ids[0];
          if (mainId) {
            newPolygons[mainId] = { initial: Array.isArray(roi) ? roi : [], current: [] };
            newImageSizes[mainId] = { naturalWidth: 0, naturalHeight: 0 };
          }
        }
        setPolygons(newPolygons);
        setImageSizes(newImageSizes);
        fetchSnapshots(cameraData);
        await fetchOverrides();
        await fetchShifts();
        await fetchBreaks();
      } catch (err) {
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchCameraData();
    return () => { Object.values(abortControllersRef.current).forEach(ctrl => ctrl.abort()); }
  }, [cameraId, t, fetchNamesForSourceCameras, fetchSnapshots, getSourceIds]);

  useEffect(() => {
    let intervalId;
    if (isLive && camera) intervalId = setInterval(() => fetchSnapshots(camera), 1000);
    return () => clearInterval(intervalId);
  }, [isLive, camera, fetchSnapshots]);

  useEffect(() => {
    let isMounted = true;
    let timerId;

    const fetchRoiDebugInfo = async () => {
      if (!isMounted || !roiDebugMode) return;

      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/api/cameras/${cameraId}/roi-debug-info/`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          setRoiDebugInfo(res.data);
          // Делаем следующий запрос только когда пришел успешный ответ
          timerId = setTimeout(fetchRoiDebugInfo, 2000);
        }
      } catch (err) {
        if (isMounted) {
          // Если сервер реально недоступен (500 ошибка), не закрываем окно, а просто ждем 5 секунд и пробуем снова
          timerId = setTimeout(fetchRoiDebugInfo, 5000);
        }
      }
    };

    if (roiDebugMode) {
      setRoiDebugInfo(null);
      fetchRoiDebugInfo();
    } else {
      setRoiDebugInfo(null);
      clearTimeout(timerId);
    }

    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [roiDebugMode, cameraId]);

  const handleImageLoaded = (id, { naturalWidth, naturalHeight }) => {
    setImageSizes((prev) => ({ ...prev, [id]: { naturalWidth, naturalHeight } }));
    setPolygons((prev) => {
      const targetPoly = prev[id];
      if (!targetPoly) return prev;
      const initialRelative = targetPoly.initial || [];
      const absolutePoints = initialRelative.map((p) => ({ x: p.x * naturalWidth, y: p.y * naturalHeight }));
      if (targetPoly.current.length === 0 && initialRelative.length > 0) return { ...prev, [id]: { ...targetPoly, current: absolutePoints } };
      return prev;
    });
  };

  const handleSaveRoi = async () => {
    if (!camera) return;
    const toRelative = (points, size) => (!size || !size.naturalWidth) ? [] : points.map((p) => ({ x: p.x / size.naturalWidth, y: p.y / size.naturalHeight }));
    const ids = getSourceIds(camera);
    let payloadRoi;
    if (camera.is_combined || camera.is_combined_camera || (camera.additional_cameras && camera.additional_cameras.length > 0)) {
      payloadRoi = {};
      ids.forEach((id) => { payloadRoi[id] = toRelative(polygons[id]?.current || [], imageSizes[id]); });
    } else {
      payloadRoi = toRelative(polygons[ids[0]]?.current || [], imageSizes[ids[0]]);
    }

    let payloadQueue = camera.operating_mode === "cafe" ? toRelative(polygons.queue?.current || [], imageSizes.queue?.naturalWidth ? imageSizes.queue : imageSizes[ids[0]]) : [];

    try {
      await axios.patch(`/api/cameras/${cameraId}/`, { roi_polygon: payloadRoi, queue_roi_polygon: payloadQueue }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setPolygons((prev) => {
        const updated = { ...prev };
        ids.forEach((id) => { if (updated[id]) updated[id].initial = toRelative(updated[id].current, imageSizes[id]); });
        if (updated.queue) updated.queue.initial = toRelative(updated.queue.current, imageSizes.queue?.naturalWidth ? imageSizes.queue : imageSizes[ids[0]]);
        return updated;
      });
      toast.success(t("camera.roi_saved"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleResetRoi = (id) => {
    const size = imageSizes[id]?.naturalWidth ? imageSizes[id] : imageSizes[getSourceIds(camera)[0]];
    if (!size || !size.naturalWidth) return;
    setPolygons((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        current: (prev[id]?.initial || []).map((p) => ({ x: p.x * size.naturalWidth, y: p.y * size.naturalHeight }))
      }
    }));
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "enable_blur" && !checked) {
      setBlurWarningModalVisible(true);
      return;
    }
    setSettings((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSaveSettings = async () => {
    try {
      const payload = {
        ...settings,
        person_roi_threshold: parseFloat(settings.person_roi_threshold) / 100,
        person_roi_occupancy_threshold: parseFloat(settings.person_roi_occupancy_threshold) / 100,
        person_confidence_threshold: parseFloat(settings.person_confidence_threshold) / 100,
        vehicle_roi_threshold: parseFloat(settings.vehicle_roi_threshold) / 100,
        vehicle_roi_occupancy_threshold: parseFloat(settings.vehicle_roi_occupancy_threshold) / 100,
        vehicle_confidence_threshold: parseFloat(settings.vehicle_confidence_threshold) / 100,
        vehicle_log_merge_seconds: parseInt(settings.vehicle_log_merge_seconds, 10),
        orb_similarity_threshold: parseInt(settings.orb_similarity_threshold, 10)
      };
      const response = await axios.patch(`/api/cameras/${cameraId}/`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setCamera(response.data);
      toast.success(t("camera.settings_saved_success"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleSegmentDoubleClick = useCallback((itemId, exactTimeIso) => {
    let targetTimeIso = exactTimeIso;

    const rawTimeline = activeTimelineData;
    if (!targetTimeIso && itemId !== null && rawTimeline && rawTimeline[itemId]) {
      targetTimeIso = rawTimeline[itemId].start;
    }

    if (targetTimeIso) {
      setMonitorConfig({
        visible: true,
        mode: 'archive',
        initialIsoTime: targetTimeIso
      });
    }
  }, [activeTimelineData]);

  const fetchReports = async (start = startDate, end = endDate) => {
    if (dayjs(end).isBefore(dayjs(start))) { toast.warn(t("common.error")); return; }
    setLoadingAnalytics(true);
    setAnalytics(null);
    setVideos([]);

    try {
      const token = localStorage.getItem("token");
      const offset = camera?.timezone_offset || 0;
      const reqStartIso = formatToCameraUTC(start, offset);
      const reqEndIso = formatToCameraUTC(end, offset);
      const analyticsParams = new URLSearchParams({ camera_id: cameraId, start_datetime: reqStartIso, end_datetime: reqEndIso }).toString();

      const res = await axios.get(`/api/analytics/?${analyticsParams}`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(res.data);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleAddOverride = async () => {
    if (!newOverride.date) return toast.warn(t("common.fill_required"));
    const dateString = dayjs(newOverride.date).format("YYYY-MM-DD");
    if (overrides.some((ov) => ov.date === dateString)) return toast.error(t("common.error"));
    try {
      await axios.post("/api/date-overrides/", { camera: cameraId, date: dateString, is_working: newOverride.is_working, description: newOverride.description }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      await fetchOverrides();
      setNewOverride({ date: new Date(), is_working: false, description: "" });
      toast.success(t("camera.exception_added"));
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleDeleteOverride = async (overrideId) => {
    if (!window.confirm(t("dashboard.confirm_delete"))) return;
    try {
      await axios.delete(`/api/date-overrides/${overrideId}/`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setOverrides(prev => prev.filter(ov => ov.id !== overrideId));
      toast.success(t("camera.exception_deleted"));
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleSaveName = async () => {
    if (!editingNameValue.trim()) return toast.warn(t("common.fill_required"));
    try {
      await axios.patch(`/api/cameras/${cameraId}/`, { name: editingNameValue }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setCamera(prev => ({ ...prev, name: editingNameValue }));
      setIsEditingName(false);
      toast.success(t("camera.edit_name_success"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleMoveLog = async () => {
    if (!moveModal.log || moveModal.targetGroupId === "") return toast.warn(t("common.fill_required"));
    try {
      await axios.post(`/api/cameras/${cameraId}/move-vehicle-log/`, { log_id: moveModal.log.id, target_log_id: moveModal.targetGroupId }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success(t("common.success"));
      setMoveModal({ visible: false, log: null, targetGroupId: "" });
      fetchReports();
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      await axios.post(`/api/cameras/${cameraId}/recalculate-analytics/`, { start_date: formatToCameraUTC(recalculationDate, camera?.timezone_offset || 0) }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success("Recalculation started");
      setRecalculateModalVisible(false);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleLlmSync = async () => {
    setIsLlmSyncing(true);
    try {
      await axios.post(`/api/cameras/${cameraId}/recalculate-llm-analytics/`, { start_date: formatToCameraUTC(llmSyncDate, camera?.timezone_offset || 0) }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success("LLM sync started");
      setLlmModalVisible(false);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setIsLlmSyncing(false);
    }
  };

  // --- ОБРАБОТЧИКИ СМЕН И ПЕРЕРЫВОВ ---
  const handleAddShift = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/cameras/${cameraId}/shifts/`, newShift, { headers: { Authorization: `Bearer ${token}` } });
      setShifts(prev => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      toast.success("Смена добавлена");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDeleteShift = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/cameras/${cameraId}/shifts/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setShifts(prev => prev.filter(s => s.id !== id));
      toast.success("Смена удалена");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleAddBreak = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/cameras/${cameraId}/breaks/`, newBreak, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks(prev => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setNewBreak({ start_time: "", end_time: "", description: "" });
      toast.success("Перерыв добавлен");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDeleteBreak = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/cameras/${cameraId}/breaks/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks(prev => prev.filter(b => b.id !== id));
      toast.success("Перерыв удален");
    } catch (err) { toast.error(t("common.error")); }
  };
  // ------------------------------------

  const formatLocalTime = (utcTime) => {
    if (!utcTime) return "-";
    const d = new Date(utcTime);
    return dayjs(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours() + 3 + (camera?.timezone_offset || 0), d.getUTCMinutes(), d.getUTCSeconds())).format("DD.MM HH:mm");
  };

  if (loading) return <Loading name={t("common.loading")} />;
  const sourceCameraIds = getSourceIds(camera);

  const monitorCameras = sourceCameraIds.map(id => ({
    id: id,
    name: sourceCameraNames[id] || `Камера ${id}`,
    timezone_offset: camera.timezone_offset
  }));

  const hintStyle = { borderBottom: '1px dashed #999', cursor: 'help', width: 'fit-content' };

  return (
    <div className="bg-light min-vh-100 p-4" style={{ position: "relative" }}>
      <CContainer fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          {isEditingName ? (
            <CInputGroup style={{ maxWidth: "400px" }}>
              <CFormInput
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
              />
              <CButton color="success" onClick={handleSaveName} className="text-white">
                <CIcon icon={cilCheck} />
              </CButton>
              <CButton color="secondary" onClick={() => setIsEditingName(false)}>
                <CIcon icon={cilX} />
              </CButton>
            </CInputGroup>
          ) : (
            <div className="d-flex align-items-center">
              <h2 className="mb-0 me-3">{camera?.name}</h2>
              {hasAdminRights && (
                <CButton color="light" size="sm" onClick={() => { setEditingNameValue(camera.name); setIsEditingName(true); }} title={t("common.edit")}>
                  <CIcon icon={cilPencil} />
                </CButton>
              )}
            </div>
          )}
          <div style={{ display: "flex" }}>
            <CButton color="secondary" as={Link} to="/dashboard">
              <CIcon icon={cilArrowLeft} className="me-2" /> {t("common.back")}
            </CButton>
            <LanguageSwitcher />
          </div>
        </div>

        {camera.is_duplicate && !camera.is_combined && !camera.is_combined_camera && (
          <CAlert color="info" className="mb-4">
            <CIcon icon={cilInfo} className="me-2" />
            {t("camera.child_post_alert", { parentId: camera.parent })}
          </CAlert>
        )}

        {(camera.is_combined || camera.is_combined_camera || (camera.additional_cameras && camera.additional_cameras.length > 0)) && (
          <CAlert color="success" className="mb-4">
            <CIcon icon={cilInfo} className="me-2" />
            <strong>Совмещенный пост:</strong> {sourceCameraIds.map((id) => sourceCameraNames[id] || `Камера ${id}`).join(" + ")}
          </CAlert>
        )}

        <CRow>
          {/* ЛЕВАЯ КОЛОНКА (Настройки и ROI) */}
          <CCol xl={5} className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex gap-3 align-items-center">
                <CFormCheck id="liveViewCheck" label={t("camera.live_view")} checked={isLive} onChange={() => setIsLive(!isLive)} />
                <CFormCheck id="showRoiCheck" label={t("camera.show_roi")} checked={showRoi} onChange={() => setShowRoi(!showRoi)} />
                <CFormCheck id="debugRoiCheck" label={t("camera.debug_roi")} checked={roiDebugMode} onChange={() => setRoiDebugMode(!roiDebugMode)} />

                {/* КНОПКА МУЛЬТИ-МОНИТОРА */}
                {sourceCameraIds.length > 1 && (
                  <CButton color="primary" size="sm" className="ms-2 text-white" onClick={() => setMonitorConfig({ visible: true, mode: 'live', date: new Date(), timeSeconds: 12 * 3600 })}>
                    <CIcon icon={cilMonitor} className="me-1" /> {t('monitor.btn_open')}
                  </CButton>
                )}
              </div>
              <CButton color="light" size="sm" onClick={() => fetchSnapshots(camera)}>
                <CIcon icon={cilCamera} />
              </CButton>
            </div>

            {sourceCameraIds.map((id, index) => (
              <div key={id} className={index === 0 ? "" : "mt-3"}>
                <RoiEditor
                  snapshotUrl={snapshots[id]}
                  polygonPoints={polygons[id] || { initial: [], current: [] }}
                  setPolygonPoints={(np) => setPolygons(p => ({ ...p, [id]: np }))}
                  isLive={isLive}
                  showRoi={showRoi}
                  onSave={handleSaveRoi}
                  onReset={() => handleResetRoi(id)}
                  onClear={() => setPolygons(p => ({ ...p, [id]: { ...p[id], current: [] } }))}
                  cameraName={sourceCameraNames[id] || `Камера ${id}`}
                  onImageLoad={(dim) => handleImageLoaded(id, dim)}
                  readOnly={!hasAdminRights}
                />
              </div>
            ))}

            {/* Зона очереди только для кафе */}
            {camera.operating_mode === "cafe" && (
              <div className="mt-3">
                <RoiEditor
                  snapshotUrl={snapshots[sourceCameraIds[0]]}
                  polygonPoints={polygons.queue || { initial: [], current: [] }}
                  setPolygonPoints={(np) => setPolygons(p => ({ ...p, queue: np }))}
                  isLive={isLive}
                  showRoi={showRoi}
                  onSave={handleSaveRoi}
                  onReset={() => handleResetRoi("queue")}
                  onClear={() => setPolygons(p => ({ ...p, queue: { ...p.queue, current: [] } }))}
                  cameraName={t("camera.queue_zone", "Зона очереди")}
                  onImageLoad={(dim) => handleImageLoaded("queue", dim)}
                  readOnly={!hasAdminRights}
                />
              </div>
            )}

            {/* Блок настроек только для админа НОВОЕ */}
            {hasAdminRights && (
              <CameraSettings
                camera={camera}
                settings={settings}
                handleSettingsChange={handleSettingsChange}
                handleSaveSettings={handleSaveSettings}
                t={t}
                i18n={i18n}
                overrides={overrides}
                loadingOverrides={loadingOverrides}
                newOverride={newOverride}
                setNewOverride={setNewOverride}
                handleAddOverride={handleAddOverride}
                handleDeleteOverride={handleDeleteOverride}
                shifts={shifts}
                newShift={newShift}
                setNewShift={setNewShift}
                handleAddShift={handleAddShift}
                handleDeleteShift={handleDeleteShift}
                breaks={breaks}
                newBreak={newBreak}
                setNewBreak={setNewBreak}
                handleAddBreak={handleAddBreak}
                handleDeleteBreak={handleDeleteBreak}
              />
            )}
          </CCol>

          {/* ПРАВАЯ КОЛОНКА (Аналитика) */}
          <CCol xl={7}>
            <CCard className="mb-4">
              <CCardHeader className="d-flex justify-content-between align-items-center">
                {t("camera.analytics_archive")}
                {/* ТОЛЬКО ДЛЯ АДМИНОВ НОВОЕ */}
                {hasAdminRights && (
                  <CButtonGroup>
                    {camera?.operating_mode === "auto_service" && (
                      <CButton color="info" variant="outline" size="sm" onClick={() => setLlmModalVisible(true)} className="ms-2">
                        <CIcon icon={cilCommentBubble} className="me-1" /> {t("camera.sync_llm")}
                      </CButton>
                    )}
                    <CButton color="warning" variant="outline" size="sm" onClick={() => setRecalculateModalVisible(true)}>
                      <CIcon icon={cilSync} className="me-1" /> {t("camera.recalc_all")}
                    </CButton>
                  </CButtonGroup>
                )}
              </CCardHeader>
              <CCardBody>
                <div className="bg-light p-3 rounded border mb-4">
                  <CRow className="g-3">
                    <CCol md={6}>
                      <CFormLabel className="fw-bold mb-1">{t("common.date_start")}</CFormLabel>
                      <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        showTimeSelect
                        className="form-control w-100"
                        dateFormat="dd.MM.yyyy HH:mm"
                        locale={i18n.language === "ru" ? "ru" : "en"}
                      />
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel className="fw-bold mb-1">{t("common.date_end")}</CFormLabel>
                      <DatePicker
                        selected={endDate}
                        onChange={setEndDate}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        showTimeSelect
                        className="form-control w-100"
                        dateFormat="dd.MM.yyyy HH:mm"
                        locale={i18n.language === "ru" ? "ru" : "en"}
                      />
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel className="fw-bold mb-1">{t("multi_cam.min_percentage_label", "Мин. % времени для учета группы")}</CFormLabel>
                      <CInputGroup>
                        <CFormInput type="number" min="0" max="100" value={minPercentage} onChange={(e) => setMinPercentage(e.target.value)} />
                        <span className="input-group-text">%</span>
                      </CInputGroup>
                    </CCol>
                    <CCol md={6} className="d-flex align-items-end">
                      <CButton color="primary" onClick={() => fetchReports()} disabled={loadingAnalytics} className="w-100 py-2 fw-bold">
                        {loadingAnalytics ? <CSpinner size="sm" /> : <><CIcon icon={cilChart} className="me-2" />{t("common.get_report")}</>}
                      </CButton>
                    </CCol>
                  </CRow>
                </div>

                {/* Динамическая сводка */}
                {analytics && !loadingAnalytics && dynamicSummary && (
                  <>
                    <CCard className="mb-4 bg-light shadow-sm border-secondary border-top-5">
                      <CCardHeader className="fw-bold text-uppercase">{t("camera.dynamic_summary_title")}</CCardHeader>
                      <CCardBody>
                        <CRow>
                          <CCol md={6}>
                            <CListGroup flush className="bg-transparent">
                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.max_people")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.max_people_working")}</span>
                                </CTooltip>
                                <span className="fs-5 fw-bold">{dynamicSummary.maxPeople} {t("camera.people_unit")}</span>
                                <span className="ms-2 badge bg-info text-dark">{">"} {minPercentage}% {t("camera.of_time")}</span>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.avg_people")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.avg_people")}</span>
                                </CTooltip>
                                <span className="fs-5 fw-bold text-primary">{dynamicSummary.weightedAveragePeople.toFixed(2)} {t("camera.people_unit")}</span>
                                <small className="d-block text-muted fst-italic">{t("camera.weighted_average")}</small>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.downtime")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.employee_absence")}</span>
                                </CTooltip>
                                <span className="fs-5 fw-bold">{formatDuration(dynamicSummary.totalDowntime)}</span>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.efficiency_activity")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.efficiency_activity")}</span>
                                </CTooltip>
                                <span className={`fs-5 fw-bold ${dynamicSummary.efficiency > 50 ? "text-success" : "text-danger"}`}>
                                  {dynamicSummary.efficiency.toFixed(1)}%
                                </span>
                                <small className="d-block text-muted fst-italic">{t("camera.excluding_phone_chair")}</small>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.efficiency_presence")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.efficiency_presence")}</span>
                                </CTooltip>
                                <span className={`fs-5 fw-bold ${dynamicSummary.presenceEfficiency > 50 ? "text-success" : "text-danger"}`}>
                                  {dynamicSummary.presenceEfficiency.toFixed(1)}%
                                </span>
                                <small className="d-block text-muted fst-italic">{t("camera.work_to_downtime")}</small>
                              </CListGroupItem>

                              {/* КПД Станок */}
                              {camera?.operating_mode === "szsm" && (
                                <CListGroupItem className="bg-transparent ps-0">
                                  <CTooltip content={t("tooltips.efficiency_machine")}>
                                    <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.efficiency_machine_szsm")}</span>
                                  </CTooltip>
                                  <span className={`fs-5 fw-bold ${dynamicSummary.machineEfficiency > 50 ? "text-success" : "text-danger"}`}>
                                    {dynamicSummary.machineEfficiency.toFixed(1)}%
                                  </span>
                                  <small className="d-block text-muted fst-italic">{t("camera.work_to_total_machine")}</small>
                                </CListGroupItem>
                              )}
                            </CListGroup>
                          </CCol>

                          <CCol md={6}>
                            <CListGroup flush className="bg-transparent">
                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.total_work")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.total_work_man_hours")}</span>
                                </CTooltip>
                                <span className="fs-5 fw-bold text-success">{formatDuration(dynamicSummary.totalWork)}</span>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.chair")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.loss_chair")}</span>
                                </CTooltip>
                                <span className="fs-5 fw-bold text-warning">{formatDuration(dynamicSummary.totalChair)}</span>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={t("tooltips.phone")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.loss_phone")}</span>
                                </CTooltip>
                                <span className="fs-5 fw-bold text-danger">{formatDuration(dynamicSummary.totalPhone)}</span>
                              </CListGroupItem>

                              {/* Время Станок */}
                              {camera?.operating_mode === "szsm" && (
                                <CListGroupItem className="bg-transparent ps-0">
                                  <span className="text-muted small d-block text-uppercase">{t("camera.machine_work_time")}</span>
                                  <span className="fs-5 fw-bold text-success">{formatDuration(dynamicSummary.totalMachineWork)}</span>
                                </CListGroupItem>
                              )}

                              {/* Общение с менеджером */}
                              {camera?.operating_mode === "manager" && (
                                <CListGroupItem className="bg-transparent ps-0">
                                  <span className="text-muted small d-block text-uppercase">{t("camera.manager_communication")}</span>
                                  <span className="fs-5 fw-bold text-info">{formatDuration(dynamicSummary.totalManager)}</span>
                                </CListGroupItem>
                              )}

                            </CListGroup>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>

                    {/* Сводка по дням для кафе и менеджера */}
                    {["cafe", "manager"].includes(analytics.mode) && dailySummary.length > 0 && (
                      <CCard className="mb-4 shadow-sm border-secondary border-top-5">
                        <CCardHeader className="fw-bold text-uppercase">{t("camera.daily_summary_title", "Сводка по дням")}</CCardHeader>
                        <CCardBody>
                          <CTable hover responsive bordered>
                            <CTableHead color="light">
                              <CTableRow>
                                <CTableHeaderCell>{t("camera.date")}</CTableHeaderCell>
                                <CTableHeaderCell>{t("camera.point_opened")}</CTableHeaderCell>
                                <CTableHeaderCell>{t("camera.point_closed")}</CTableHeaderCell>
                                <CTableHeaderCell>{t("camera.first_client")}</CTableHeaderCell>
                                <CTableHeaderCell>{t("camera.last_client")}</CTableHeaderCell>
                              </CTableRow>
                            </CTableHead>
                            <CTableBody>
                              {dailySummary.map((day) => (
                                <CTableRow key={day.date}>
                                  <CTableDataCell><strong>{dayjs(day.date).format("DD.MM.YYYY")}</strong></CTableDataCell>
                                  <CTableDataCell>{day.pointOpen ? day.pointOpen.format("HH:mm") : "-"}</CTableDataCell>
                                  <CTableDataCell>{day.pointClose ? day.pointClose.format("HH:mm") : "-"}</CTableDataCell>
                                  <CTableDataCell>{day.firstClient ? day.firstClient.format("HH:mm") : "-"}</CTableDataCell>
                                  <CTableDataCell>{day.lastClient ? day.lastClient.format("HH:mm") : "-"}</CTableDataCell>
                                </CTableRow>
                              ))}
                            </CTableBody>
                          </CTable>
                        </CCardBody>
                      </CCard>
                    )}

                    <div className="mb-3 p-2 bg-light border rounded d-flex gap-3 align-items-center flex-wrap">
                      <span className="text-muted small text-uppercase fw-bold me-2"><CIcon icon={cilOptions} /> {t("camera.view_toggle_label")}</span>
                      <CFormCheck type="radio" id="viewToggleGeneral" name="viewToggle" label={t("camera.view_general")} checked={activeView === "general"} onChange={() => setActiveView("general")} />
                      <CFormCheck type="radio" id="viewToggleChair" name="viewToggle" label={t("camera.view_chair")} checked={activeView === "chair"} onChange={() => setActiveView("chair")} />
                      <CFormCheck type="radio" id="viewTogglePhone" name="viewToggle" label={t("camera.view_phone")} checked={activeView === "phone"} onChange={() => setActiveView("phone")} />

                      {camera.operating_mode === "cafe" && (
                        <CFormCheck type="radio" id="viewToggleQueue" name="viewToggle" label={t("camera.view_queue", "В очереди")} checked={activeView === "queue"} onChange={() => setActiveView("queue")} />
                      )}

                      {camera.operating_mode === "szsm" && (
                        <CFormCheck type="radio" id="viewToggleMachine" name="viewToggle" label={t("camera.view_machine", "Работа станка")} checked={activeView === "machine"} onChange={() => setActiveView("machine")} />
                      )}

                      {camera.operating_mode === "manager" && (
                        <CFormCheck type="radio" id="viewToggleManager" name="viewToggle" label={t("camera.view_manager", "Работа с клиентом")} checked={activeView === "manager"} onChange={() => setActiveView("manager")} />
                      )}
                    </div>

                    {/* Графики аналитики для автосервиса */}
                    {analytics.mode === "auto_service" && (
                      <>
                        <CCard className="mb-4">
                          <CCardHeader>{t("camera.vehicle_summary_title")}</CCardHeader>
                          <CCardBody>
                            <CListGroup flush>
                              <CListGroupItem><strong>{t("camera.total_entries")}:</strong> {analytics.vehicle_entries_count}</CListGroupItem>
                              <CListGroupItem><strong>{t("camera.unique_vehicles")}:</strong> {groupedVehicleLogs.length}</CListGroupItem>
                              <CListGroupItem><strong>{t("camera.total_downtime")}:</strong> {formatDuration(analytics.general_stats.total_downtime_seconds)}</CListGroupItem>
                              <CListGroupItem><strong>{t("camera.total_idle")}:</strong> {formatDuration(analytics.general_stats.total_idle_time_seconds)}</CListGroupItem>
                            </CListGroup>
                          </CCardBody>
                        </CCard>

                        {activeView === "general" && generalData && (
                          <AnalyticsBlock title={t("camera.activity_general_title")} analyticsData={generalData} classIdToMeta={autoServiceClassIdToMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="general" operatingMode={analytics.mode} />
                        )}
                        {activeView === "chair" && chairData && (
                          <AnalyticsBlock title={t("camera.activity_chair_title")} analyticsData={chairData} timelineDataOverride={chairTimelineToShow} classIdToMeta={chairMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="chair" operatingMode={analytics.mode} />
                        )}
                        {activeView === "phone" && phoneData && (
                          <AnalyticsBlock title={t("camera.activity_phone_title")} analyticsData={phoneData} timelineDataOverride={phoneTimelineToShow} classIdToMeta={phoneMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="phone" operatingMode={analytics.mode} />
                        )}
                      </>
                    )}

                    {/* Графики аналитики для остальных режимов */}
                    {["personnel_control", "cafe", "szsm", "manager"].includes(analytics.mode) && (
                      <>
                        {activeView === "general" && generalData && (
                          <AnalyticsBlock title={t("camera.activity_general_title")} analyticsData={generalData} classIdToMeta={personnelGeneralMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="general" operatingMode={analytics.mode} />
                        )}
                        {activeView === "chair" && chairData && (
                          <AnalyticsBlock title={t("camera.activity_chair_title")} analyticsData={chairData} classIdToMeta={chairMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="chair" operatingMode={analytics.mode} />
                        )}
                        {activeView === "phone" && phoneData && (
                          <AnalyticsBlock title={t("camera.activity_phone_title")} analyticsData={phoneData} classIdToMeta={phoneMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="phone" operatingMode={analytics.mode} />
                        )}
                        {activeView === "queue" && queueData && (
                          <AnalyticsBlock title={t("camera.activity_queue_title", "Активность: Очередь")} analyticsData={queueData} classIdToMeta={queueMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="queue" operatingMode={analytics.mode} />
                        )}
                        {activeView === "machine" && machineData && (
                          <AnalyticsBlock title={t("camera.activity_machine_title", "Активность: Станок")} analyticsData={machineData} timelineDataOverride={machineTimelineToShow} classIdToMeta={machineMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="machine" operatingMode={analytics.mode} />
                        )}
                        {activeView === "manager" && managerData && (
                          <AnalyticsBlock title={t("camera.activity_manager_title", "Активность: Общение с клиентом")} analyticsData={managerData} timelineDataOverride={managerTimelineToShow} classIdToMeta={managerMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="manager" operatingMode={analytics.mode} />
                        )}
                      </>
                    )}

                    {/* LLM логи */}
                    {camera?.operating_mode === "auto_service" && (
                      <>
                        <h5 className="mt-4">{t("camera.analytics_llm")}</h5>
                        {groupedVehicleLogs.length > 0 ? (
                          <CAccordion flush>
                            {groupedVehicleLogs.map((group, i) => (
                              <CAccordionItem itemKey={group.id} key={group.id}>
                                <CAccordionHeader className="clickable-header">
                                  <div className="d-flex align-items-center w-100 justify-content-between pe-3">
                                    <div>
                                      <CIcon icon={cilCarAlt} className="me-2" />
                                      <strong>{t("camera.vehicle_group_label", { index: i + 1 })}</strong>
                                      <CBadge color="info" className="ms-2">{t("camera.visits_count", { count: group.logs.length })}</CBadge>
                                      {group.total_man_hours > 0 && <CBadge color="warning" className="ms-3">{t("camera.man_hours", { hours: group.total_man_hours.toFixed(1) })}</CBadge>}
                                    </div>
                                    <div>
                                      <CButton size="sm" color="light" className="me-3" onClick={(e) => { e.stopPropagation(); setStartDate(dayjs(group.startTime).toDate()); setEndDate(dayjs(group.endTime).toDate()); fetchReports(dayjs(group.startTime).toDate(), dayjs(group.endTime).toDate()); }}>
                                        <CIcon icon={cilChart} />
                                      </CButton>
                                      <small className="text-muted">{formatLocalTime(group.startTime)} - {formatLocalTime(group.endTime)}</small>
                                    </div>
                                  </div>
                                </CAccordionHeader>
                                <CAccordionBody>
                                  <CAccordion flush>
                                    {group.logs.map((log) => {
                                      const comment = analytics.vehicle_analysis_comments.find(c => c.first_vehicle_log_id === log.id);
                                      return (
                                        <CAccordionItem itemKey={log.id} key={log.id}>
                                          <CAccordionHeader className="clickable-header">
                                            <div className="d-flex w-100 justify-content-between align-items-center pe-3">
                                              <div>
                                                <CIcon icon={cilTask} className="me-2 text-primary" />
                                                <strong>{t("camera.visit_label", { id: log.id })} : {formatLocalTime(log.entry_time)} - {formatLocalTime(log.exit_time)}</strong>
                                              </div>
                                              <div>
                                                {/* НОВОЕ ТОЛЬКО ДЛЯ АДМИНОВ КНОПКА */}
                                                {hasAdminRights && (
                                                  <CButton size="sm" color="light" className="me-2" onClick={(e) => { e.stopPropagation(); setMoveModal({ visible: true, log, targetGroupId: "" }); }}>
                                                    <CIcon icon={cilMove} />
                                                  </CButton>
                                                )}
                                                <CButton size="sm" color="light" className="me-2" onClick={(e) => { e.stopPropagation(); setStartDate(dayjs(log.entry_time).toDate()); setEndDate(dayjs(log.exit_time).toDate()); fetchReports(dayjs(log.entry_time).toDate(), dayjs(log.exit_time).toDate()); }}>
                                                  <CIcon icon={cilChart} />
                                                </CButton>
                                              </div>
                                            </div>
                                          </CAccordionHeader>
                                          {/* <CAccordionBody>
                                            {log.work_analysis_text ? (
                                              <pre style={{ whiteSpace: "pre-wrap", background: "#f8f9fa", padding: "10px" }}>{log.work_analysis_text}</pre>
                                            ) : (
                                              <div className="text-center text-muted p-3"><CIcon icon={cilWarning} className="me-2" />{t("camera.no_work_analysis")}</div>
                                            )}
                                            {comment && (
                                              <div className="mt-3 pt-3 border-top">
                                                <p><strong>LLM:</strong> {comment.comment_text}</p>
                                                <p><strong>ORB:</strong><CBadge color={comment.is_same_vehicle ? "success" : "danger"} className="ms-2">{comment.similarity_percentage} {t("camera.matches")}</CBadge></p>
                                              </div>
                                            )}
                                          </CAccordionBody> */}
                                          <CAccordionBody>

                                            {/* НОВОЕ: ОТОБРАЖЕНИЕ СКРИНШОТОВ (ФОТО ФИКСАЦИЯ) */}
                                            <div className="d-flex flex-wrap gap-3 mb-3">
                                              {log.first_frame_for_analysis_url && (
                                                <div className="text-center" style={{ flex: '1 1 45%' }}>
                                                  <span className="text-muted small d-block mb-1">{t("camera.start_frame", "Фото в начале")}</span>
                                                  <img src={log.first_frame_for_analysis_url} alt="Start" style={{ width: '100%', borderRadius: '6px', border: '1px solid #ccc' }} />
                                                </div>
                                              )}
                                              {log.last_frame_for_analysis_url && (
                                                <div className="text-center" style={{ flex: '1 1 45%' }}>
                                                  <span className="text-muted small d-block mb-1">{t("camera.end_frame", "Фото в конце")}</span>
                                                  <img src={log.last_frame_for_analysis_url} alt="End" style={{ width: '100%', borderRadius: '6px', border: '1px solid #ccc' }} />
                                                </div>
                                              )}
                                            </div>

                                            {log.work_analysis_text ? (
                                              <pre style={{ whiteSpace: "pre-wrap", background: "#f8f9fa", padding: "10px", border: "1px solid #dee2e6", borderRadius: "6px" }}>
                                                {log.work_analysis_text}
                                              </pre>
                                            ) : (
                                              <div className="text-center text-muted p-3"><CIcon icon={cilWarning} className="me-2" />{t("camera.no_work_analysis")}</div>
                                            )}
                                            {comment && (
                                              <div className="mt-3 pt-3 border-top">
                                                <p><strong>LLM:</strong> {comment.comment_text}</p>
                                                <p><strong>ORB:</strong><CBadge color={comment.is_same_vehicle ? "success" : "danger"} className="ms-2">{comment.similarity_percentage} {t("camera.matches")}</CBadge></p>
                                              </div>
                                            )}
                                          </CAccordionBody>
                                        </CAccordionItem>
                                      );
                                    })}
                                  </CAccordion>
                                </CAccordionBody>
                              </CAccordionItem>
                            ))}
                          </CAccordion>
                        ) : <p className="text-muted">No vehicle visits.</p>}
                      </>
                    )}
                  </>
                )}
                {!analytics && !loadingAnalytics && <p className="text-center text-muted">{t("camera.select_period_msg")}</p>}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>

      {/* DEBUG DRAGGABLE WINDOW */}
        { roiDebugMode && (
          <Draggable handle=".roi-debug-handle" bounds="parent">
            <div style={{ position: "absolute", top: "150px", left: "50px", width: "350px", maxHeight: "400px", zIndex: 1050, background: "white", border: "1px solid #dee2e6", borderRadius: "0.375rem", display: "flex", flexDirection: "column" }}>
              <div className="roi-debug-handle" style={{ padding: "0.75rem 1rem", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6", cursor: "move", display: "flex", justifyContent: "space-between" }}>
                <h6 className="mb-0">{t("camera.roi_debug_title")}</h6>
                <CButton color="secondary" variant="ghost" size="sm" onClick={() => setRoiDebugMode(false)}>
                  <CIcon icon={cilX} />
                </CButton>
              </div>
              <div style={{ padding: "1rem", overflowY: "auto", fontFamily: "monospace", fontSize: "13px" }}>
                {!roiDebugInfo ? (
                  <div className="text-center"><CSpinner size="sm" /></div>
                ) : roiDebugInfo.no_frames ? (
                  <div className="text-muted text-center py-3">Ожидание свежих кадров...<br /><small>(Возможно, камера выключена)</small></div>
                ) : roiDebugInfo.boxes_data.length === 0 ? (
                  <div className="text-muted text-center">{t("camera.roi_debug_no_objects", "Объекты не найдены")}</div>
                ) : (
                  roiDebugInfo.boxes_data.map((box, index) => (
                    <div key={index} style={{
                      borderLeft: `4px solid ${box.is_in_roi ? "#2eb85c" : "#e55353"}`,
                      paddingLeft: "8px",
                      marginBottom: "8px",
                      color: box.is_in_roi ? "#2eb85c" : "inherit"
                    }}>
                      <strong style={{ textTransform: "uppercase" }}>{box.class_name}</strong>
                      {box.is_in_roi ? " (В зоне)" : ""} <br />
                      Intersection: {(box.area_percentage * 100).toFixed(2)}% <br />
                      Occupancy: {(box.occupancy_percentage * 100).toFixed(3)}%
                    </div>
                  ))
                )}
              </div>
            </div>
          </Draggable>
        )}

      {/* Модальные окна */}
      <CameraModals
        t={t}
        i18n={i18n}
        recalculateModalVisible={recalculateModalVisible}
        setRecalculateModalVisible={setRecalculateModalVisible}
        recalculationDate={recalculationDate}
        setRecalculationDate={setRecalculationDate}
        handleRecalculate={handleRecalculate}
        isRecalculating={isRecalculating}
        llmModalVisible={llmModalVisible}
        setLlmModalVisible={setLlmModalVisible}
        llmSyncDate={llmSyncDate}
        setLlmSyncDate={setLlmSyncDate}
        handleLlmSync={handleLlmSync}
        isLlmSyncing={isLlmSyncing}
        moveModal={moveModal}
        setMoveModal={setMoveModal}
        groupedVehicleLogs={groupedVehicleLogs}
        handleMoveLog={handleMoveLog}
        blurWarningModalVisible={blurWarningModalVisible}
        setBlurWarningModalVisible={setBlurWarningModalVisible}
        handleAcceptBlurDisable={() => { setSettings(p => ({ ...p, enable_blur: false })); setBlurWarningModalVisible(false); }}
      />

      {/* --- РЕНДЕР МУЛЬТИ-МОНИТОРА --- */}
      {monitorConfig.visible && (
        <MultiCameraMonitor
          cameras={monitorCameras}
          onClose={() => setMonitorConfig(p => ({ ...p, visible: false }))}
          initialConfig={monitorConfig}
          timelineData={activeTimelineData}
          classIdToMeta={activeMeta}
          dateRange={analytics?.date_range}
        />
      )}

    </div>
  );
};

export default CameraDetailPage;